import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ToastComponent } from "../../Components/ToastComponent";
import { clientService } from "../../../Services/client.service";
import { errorHandling } from "../../../Services/error.handling";
import { formatToE164 } from "../../../Services/string.service";
import { getCompanyIdCookie } from "../../../Utils/companyIdCookie";

// Aba "Configurações" do cliente — os 5 campos desta tela vêm/vão pro GET
// e PUT /customers/{id}:
// AuthorizeEmail, AuthorizeSms, AuthorizeNegativeListing,
// AuthorizeAutomaticRestriction, AuthorizeWhatsapp.
//
// Mesmo padrão de Address.jsx: PUT /customers/{id} não aceita atualização
// parcial, então Salvar busca o cliente de novo, monta o payload COMPLETO a
// partir desse fetch (preservando tudo que não é desta aba) e só sobrescreve
// os 5 campos que esta tela realmente edita.
export default function Settings({ clientId }) {
  const { t } = useTranslation();

  const [settings, setSettings] = useState({
    eventsByPhone: false,
    billingEmailAlert: false,
    billingSmsAlert: false,
    autoRestriction: false,
    authorizeNegativation: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [toastParams, setToastParams] = useState("");

  const showToast = (title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  };

  useEffect(() => {
    let isMounted = true;

    async function loadSettings() {
      if (!clientId) return;
      const client = await clientService.fetchClient(clientId);
      if (!isMounted || !client) return;

      // Campos vêm como string "S"/"N" do GET, não booleano — Boolean("N")
      // daria true (qualquer string não-vazia é truthy em JS). Compara com
      // "S" explicitamente.
      setSettings({
        eventsByPhone: client.authorizeWhatsapp === "S",
        billingEmailAlert: client.authorizeEmail === "S",
        billingSmsAlert: client.authorizeSms === "S",
        autoRestriction: client.authorizeAutomaticRestriction === "S",
        authorizeNegativation: client.authorizeNegativeListing === "S",
      });
    }

    loadSettings();
    return () => {
      isMounted = false;
    };
  }, [clientId]);

  const toggle = (field) => {
    setSettings((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Payload completo do PUT /customers/{id}, igual Address.jsx — os campos
  // que não são desta aba vêm do cliente recém-buscado (client), pra não
  // apagar o que já estava salvo; overrides sobrescreve só os 5 campos que
  // esta tela edita de verdade.
  const buildFullPayload = (client, overrides) => ({
    RegionalId: getCompanyIdCookie(),
    ClientName: client.clientName ?? "",
    ClientTradeName: client.clientTradeName ?? "",
    Doc: client.doc ?? "",
    RepresentativeId: client.representativeId || null,
    SellerId: client.salespersonId || null,
    ServicePlanId: client.servicePlanId || null,
    MasterUserEmail: client.masterUserEmail ?? null,
    MasterUserName: null,
    MasterUserAccessLevelId: null,
    StateRegistration: client.stateRegistration ?? "",
    MunicipalRegistration: client.municipalRegistration ?? "",
    SimpleTax: client.simpleOption ? "S" : "N",
    Public: client.publicEntity ?? null,
    Email: client.responsibleEmail || null,
    PhoneNumber: formatToE164(client.phone1 ?? ""),
    SecondaryPhoneNumber: formatToE164(client.phone2 ?? ""),
    Observation: client.clientNotes ?? null,
    OperationalObservation: client.operationalNotes ?? null,
    BillingObservation: client.billingNotes ?? null,
    BirthDate: client.birthDate || null,
    PersonType: client.personType ?? "",
    Dda: client.dda ?? "",
    CreditAnalysis: client.creditAnalysis ?? "",
    CreditAnalysisDate: client.creditAnalysisDate ?? null,
    PaymentBook: client.paymentBook ?? "",
    AdministrativeRestrictionId: client.administrativeRestrictionId || null,
    AdministrativeRestrictionDate: client.administrativeRestrictionDate || null,
    AdministrativeRestrictionEndDate: client.administrativeRestrictionEndDate || null,
    LastTemporarySuspensionDate: client.lastTemporarySuspensionDate ?? null,
    // Campos S/N do schema real do PUT (não booleano — string "S"/"N").
    // GET e PUT usam nomes diferentes pra estes dois: authorizeNegativeListing
    // (GET) -> AuthorizeNegativeList (PUT); authorizeWhatsapp (GET) ->
    // Whatsapp (PUT, sem o prefixo "Authorize").
    AuthorizeEmail: client.authorizeEmail ?? "N",
    AuthorizeSms: client.authorizeSms ?? "N",
    AuthorizeNegativeList: client.authorizeNegativeListing ?? "N",
    AuthorizeAutomaticRestriction: client.authorizeAutomaticRestriction ?? "N",
    Whatsapp: client.authorizeWhatsapp ?? "N",
    ZipCode: client.postalCode1 || "",
    Street: client.address1 || "",
    Number: client.number1 || "",
    Neighborhood: client.district1 || "",
    Complement: client.complement1 || "",
    City: client.city1 || "",
    State: client.state1 || "",
    SecondaryZipCode: client.postalCode2 || "",
    SecondaryStreet: client.address2 || "",
    SecondaryNumber: client.number2 || "",
    SecondaryNeighborhood: client.district2 || "",
    SecondaryComplement: client.complement2 || "",
    SecondaryCity: client.city2 || "",
    SecondaryState: client.state2 || "",
    ...overrides,
  });

  const handleSave = async () => {
    if (!clientId) return;

    setIsSaving(true);
    try {
      const client = await clientService.fetchClient(clientId);
      if (!client || client.error) {
        showToast(t("COMMON.ERROR"), t("CUSTOMER.SETTINGS.LOAD_ERROR"), "Danger");
        return;
      }

      const payload = buildFullPayload(client, {
        AuthorizeEmail: settings.billingEmailAlert ? "S" : "N",
        AuthorizeSms: settings.billingSmsAlert ? "S" : "N",
        AuthorizeNegativeList: settings.authorizeNegativation ? "S" : "N",
        AuthorizeAutomaticRestriction: settings.autoRestriction ? "S" : "N",
        Whatsapp: settings.eventsByPhone ? "S" : "N",
      });

      const result = await clientService.updateClient(clientId, payload);

      if (!result || result.error) {
        const backendMessage = errorHandling.getErrorMessage(
          result,
          t("CUSTOMER.SETTINGS.SAVE_ERROR")
        );
        showToast(t("COMMON.ERROR"), backendMessage, "Danger");
        return;
      }

      showToast(t("COMMON.SUCCESS"), t("CUSTOMER.SETTINGS.SAVED_SUCCESS"), "Success");
    } catch (error) {
      showToast(t("COMMON.ERROR"), t("CUSTOMER.SETTINGS.SAVE_ERROR"), "Danger");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-3">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      {/* Sistema */}
      <div className="mb-4">
        <div className="bg-primary text-white px-3 py-2 mb-3">
          {t("CUSTOMER.SETTINGS.SYSTEM_TITLE")}
        </div>

        <div className="form-check mb-2">
          <input
            type="checkbox"
            className="form-check-input"
            id="settings-events-by-phone"
            checked={settings.eventsByPhone}
            onChange={() => toggle("eventsByPhone")}
          />
          <label
            className="form-check-label"
            htmlFor="settings-events-by-phone"
          >
            {t("CUSTOMER.SETTINGS.EVENTS_BY_PHONE")}
          </label>
        </div>
      </div>

      {/* Cobrança */}
      <div className="mb-4">
        <div className="bg-primary text-white px-3 py-2 mb-3">
          {t("CUSTOMER.SETTINGS.BILLING_TITLE")}
        </div>

        <div className="form-check mb-2">
          <input
            type="checkbox"
            className="form-check-input"
            id="settings-billing-email-alert"
            checked={settings.billingEmailAlert}
            onChange={() => toggle("billingEmailAlert")}
          />
          <label
            className="form-check-label"
            htmlFor="settings-billing-email-alert"
          >
            {t("CUSTOMER.SETTINGS.BILLING_EMAIL_ALERT")}
          </label>
        </div>

        <div className="form-check mb-2">
          <input
            type="checkbox"
            className="form-check-input"
            id="settings-billing-sms-alert"
            checked={settings.billingSmsAlert}
            onChange={() => toggle("billingSmsAlert")}
          />
          <label
            className="form-check-label"
            htmlFor="settings-billing-sms-alert"
          >
            {t("CUSTOMER.SETTINGS.BILLING_SMS_ALERT")}
          </label>
        </div>

        <div className="form-check mb-2">
          <input
            type="checkbox"
            className="form-check-input"
            id="settings-auto-restriction"
            checked={settings.autoRestriction}
            onChange={() => toggle("autoRestriction")}
          />
          <label
            className="form-check-label"
            htmlFor="settings-auto-restriction"
          >
            {t("CUSTOMER.SETTINGS.AUTO_RESTRICTION")}
          </label>
        </div>

        <div className="form-check mb-2">
          <input
            type="checkbox"
            className="form-check-input"
            id="settings-authorize-negativation"
            checked={settings.authorizeNegativation}
            onChange={() => toggle("authorizeNegativation")}
          />
          <label
            className="form-check-label"
            htmlFor="settings-authorize-negativation"
          >
            {t("CUSTOMER.SETTINGS.AUTHORIZE_NEGATIVATION")}
          </label>
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
          {t("CUSTOMER.SETTINGS.SAVE")}
        </button>
      </div>
    </div>
  );
}
